import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Drawer,
  Collapse
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  DevicesOther as DevicesIcon,
  FilterList as FilterIcon,
  Lightbulb as LightbulbIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  SmartToyOutlined as RobotIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Storefront as StorefrontIcon,
  HelpOutline as HelpOutlineIcon,
  ExpandLess,
  ExpandMore,
  PhoneAndroid as PhoneIcon,
  Laptop as LaptopIcon,
  TabletMac as TabletIcon,
  Camera as CameraIcon,
  BugReport as BugIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Battery90 as BatteryIcon,
  ScreenRotation as ScreenIcon,
  NetworkCheck as NetworkIcon,
  ErrorOutline as MalfunctionIcon,
  Construction as RepairIcon,
  RequestQuote as CostIcon,
  Bolt as QuickFixIcon,
  Update as UpdateIcon,
  Security as SecurityIcon,
  TipsAndUpdates as TipsIcon
} from '@mui/icons-material';
import AILogo from '../assets/logo0.png'; // Adjust the path if needed

// Replace the EnhancedRobotIcon component with this new one
const AILogoHeader = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 2,
      pl: 1
    }}
  >
    <Box
      component="img"
      src={AILogo}
      alt="AI Assistant"
      sx={{
        height: '32px',
        width: 'auto',
        filter: 'brightness(0) invert(1)',
        transition: 'all 0.3s ease'
      }}
    />
  </Box>
);

// Add this footer component
const ChatFooter = () => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 0,
      left: 280,
      right: 0,
      height: '60px',
      bgcolor: '#000000',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      zIndex: 1
    }}
  >
    <Container>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          py: 1
        }}
      >
        <Button
          startIcon={<QuestionAnswerIcon />}
          size="small"
          sx={{
            color: '#FFFFFF',
            '&:hover': {
              color: '#008744'
            }
          }}
        >
          Ask Question
        </Button>
        <Button
          startIcon={<StorefrontIcon />}
          size="small"
          sx={{
            color: '#FFFFFF',
            '&:hover': {
              color: '#008744'
            }
          }}
        >
          Marketplace
        </Button>
        <Button
          startIcon={<HelpOutlineIcon />}
          size="small"
          sx={{
            color: '#FFFFFF',
            '&:hover': {
              color: '#008744'
            }
          }}
        >
          Help
        </Button>
      </Box>
    </Container>
  </Box>
);

const SidebarMenuItem = ({ icon, text, subItems = [] }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <>
      <ListItem
        button
        onClick={() => setOpen(!open)}
        sx={{
          color: '#008744',
          '&:hover': {
            bgcolor: 'rgba(0, 135, 68, 0.1)',
          },
          borderRadius: 1,
          mb: 0.5
        }}
      >
        <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={text}
          sx={{
            '& .MuiListItemText-primary': {
              fontWeight: 500
            }
          }}
        />
        {subItems.length > 0 && (
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItem>
      
      {subItems.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems.map((item, index) => (
              <ListItem
                key={index}
                button
                sx={{
                  pl: 4,
                  color: '#FFFFFF',
                  '&:hover': {
                    bgcolor: 'rgba(0, 135, 68, 0.1)',
                    color: '#008744'
                  },
                  borderRadius: 1,
                  mb: 0.5
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// Add this new component for suggestion links
const SuggestionLink = ({ icon, text }) => (
  <ListItem
    button
    sx={{
      py: 0.5,
      px: 2,
      color: 'rgba(255,255,255,0.7)',
      '&:hover': {
        color: '#008744',
        bgcolor: 'rgba(0,135,68,0.1)',
        borderRadius: 1,
      },
      transition: 'all 0.2s ease',
    }}
  >
    <ListItemIcon 
      sx={{ 
        minWidth: '35px',
        color: 'inherit',
        fontSize: '1.2rem'
      }}
    >
      {icon}
    </ListItemIcon>
    <ListItemText 
      primary={text}
      sx={{
        '& .MuiListItemText-primary': {
          fontSize: '0.9rem',
        }
      }}
    />
  </ListItem>
);

// Update the sidebar content in your AI component
const Sidebar = () => (
  <>
    <List sx={{ mt: 2 }}>
      <SidebarMenuItem
        icon={<DevicesIcon />}
        text="Device Diagnostics"
        subItems={[
          { icon: <PhoneIcon />, text: "Smartphone Diagnostics" },
          { icon: <LaptopIcon />, text: "Laptop Diagnostics" },
          { icon: <TabletIcon />, text: "Tablet Diagnostics" },
          { icon: <SpeedIcon />, text: "Performance Analysis" },
          { icon: <MemoryIcon />, text: "Hardware Check" },
          { icon: <BatteryIcon />, text: "Battery Health" },
        ]}
      />
      
      <SidebarMenuItem
        icon={<ImageIcon />}
        text="Image Analysis"
        subItems={[
          { icon: <CameraIcon />, text: "Device Camera Scan" },
          { icon: <ScreenIcon />, text: "Screen Analysis" },
          { icon: <BugIcon />, text: "Defect Detection" },
          { icon: <AssessmentIcon />, text: "Quality Assessment" },
        ]}
      />
      
      <SidebarMenuItem
        icon={<BuildIcon />}
        text="Repair Guide"
        subItems={[
          { icon: <BuildIcon />, text: "Component Replacement" },
          { icon: <NetworkIcon />, text: "Connectivity Issues" },
          { icon: <BatteryIcon />, text: "Power Problems" },
          { icon: <ScreenIcon />, text: "Display Repairs" },
        ]}
      />
      
      <SidebarMenuItem
        icon={<LightbulbIcon />}
        text="Cost Estimation"
        subItems={[
          { icon: <AttachFileIcon />, text: "Parts Cost" },
          { icon: <BuildIcon />, text: "Labor Estimation" },
          { icon: <QuestionAnswerIcon />, text: "Repair Timeline" },
        ]}
      />
    </List>

    <Box sx={{ mt: 3, mb: 2, px: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255,255,255,0.5)',
          fontWeight: 600,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        QUICK SUGGESTIONS
      </Typography>
    </Box>

    <List dense>
      <SuggestionLink 
        icon={<SpeedIcon />} 
        text="Analyze device performance issues" 
      />
      <SuggestionLink 
        icon={<MalfunctionIcon />} 
        text="Diagnose system malfunctions" 
      />
      <SuggestionLink 
        icon={<RepairIcon />} 
        text="Get repair instructions" 
      />
      <SuggestionLink 
        icon={<CostIcon />} 
        text="Calculate repair costs" 
      />
      <SuggestionLink 
        icon={<QuickFixIcon />} 
        text="Quick troubleshooting steps" 
      />
      <SuggestionLink 
        icon={<UpdateIcon />} 
        text="Check for system updates" 
      />
      <SuggestionLink 
        icon={<SecurityIcon />} 
        text="Security diagnostics" 
      />
      <SuggestionLink 
        icon={<TipsIcon />} 
        text="Maintenance tips" 
      />
    </List>
  </>
);

const AI = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Suggested filters and prompts
  const filters = [
    { title: 'Device Diagnostics', icon: <DevicesIcon /> },
    { title: 'Image Analysis', icon: <ImageIcon /> },
    { title: 'Repair Guide', icon: <SettingsIcon /> },
    { title: 'Cost Estimation', icon: <LightbulbIcon /> },
  ];

  const suggestions = [
    "Analyze this device's performance issues",
    "What could be causing this malfunction?",
    "How to repair this component?",
    "Estimate repair costs for this issue"
  ];

  // Handle message submission
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const newMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages([...messages, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        text: "I understand you're asking about " + inputMessage + ". Let me analyze that for you...",
        sender: 'ai',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1500);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newMessage = {
          text: 'Uploaded image for analysis',
          sender: 'user',
          timestamp: new Date().toISOString(),
          image: e.target.result
        };
        setMessages([...messages, newMessage]);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0A0A0A' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            bgcolor: '#000000',
            color: '#FFFFFF',
            borderRight: '1px solid rgba(255,255,255,0.1)'
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <AILogoHeader />
          <Sidebar />
        </Box>
      </Drawer>

      {/* Main chat container with adjusted padding and margins */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'relative'
        }}
      >
        {/* Chat messages container with bottom margin */}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            overflowY: 'auto',
            marginBottom: '120px', // Increased margin to prevent content hiding
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#1A1A1A',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#333333',
              borderRadius: '4px',
            },
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                mb: 2
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  maxWidth: '70%',
                  bgcolor: message.sender === 'user' ? '#008744' : '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: message.sender === 'user' ? '#006633' : '#333',
                      width: 24,
                      height: 24,
                      mr: 1
                    }}
                  >
                    {message.sender === 'user' ? <PersonIcon /> : <AIIcon />}
                  </Avatar>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {message.sender === 'user' ? 'You' : 'AI Assistant'}
                  </Typography>
                </Box>
                
                {message.image && (
                  <Box
                    component="img"
                    src={message.image}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                )}
                
                <Typography>{message.text}</Typography>
              </Paper>
            </Box>
          ))}
          
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <CircularProgress size={20} sx={{ color: '#008744' }} />
            </Box>
          )}
        </Box>

        {/* Input area fixed above footer */}
        <Box
          sx={{
            position: 'fixed',
            bottom: '60px', // Position above footer
            left: 280,
            right: 0,
            bgcolor: '#000000',
            p: 2,
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="file-upload"
              onChange={handleFileUpload}
            />
            <label htmlFor="file-upload">
              <IconButton component="span" sx={{ color: '#008744' }}>
                <AttachFileIcon />
              </IconButton>
            </label>
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#008744',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#008744',
                  },
                },
              }}
            />
            
            <IconButton 
              onClick={handleSendMessage}
              sx={{
                bgcolor: '#008744',
                color: '#FFFFFF',
                '&:hover': {
                  bgcolor: '#006633',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Footer */}
        <ChatFooter />
      </Box>
    </Box>
  );
};

export default AI; 